import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISSED_KEY = "lazcoach_pwa_dismissed_v1";
const INSTALLED_KEY = "lazcoach_pwa_installed";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !window.MSStream
  );
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosTipOpen, setIosTipOpen] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;

    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt && Date.now() - parseInt(dismissedAt, 10) < 7 * 24 * 60 * 60 * 1000) {
      return; // dismissed within last 7 days
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      localStorage.setItem(INSTALLED_KEY, "1");
      setVisible(false);
    };
    window.addEventListener("appinstalled", installed);

    // iOS doesn't fire beforeinstallprompt - show iOS-specific tip after a delay
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => setVisible(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installed);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
    setIosTipOpen(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch (err) {
        console.warn("PWA install prompt dismissed/failed:", err);
      }
      setDeferred(null);
      setVisible(false);
    } else if (isIOS()) {
      setIosTipOpen(true);
    }
  };

  if (!visible) return null;

  return (
    <>
      <div
        data-testid="pwa-install-banner"
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50 animate-in slide-in-from-bottom duration-300"
      >
        <div className="rounded-3xl border border-stone-200 bg-stone-50/95 backdrop-blur-xl shadow-[0_24px_60px_rgba(28,25,23,0.12)] p-4 sm:p-5 flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl bg-white border border-stone-200 overflow-hidden flex-shrink-0">
            <img
              src="/icon-192.png"
              alt="Laz.coach"
              className="h-full w-full object-cover"
              draggable="false"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg text-stone-900 leading-tight">
              Install Laz.coach
            </div>
            <div className="text-xs text-stone-500 mt-0.5">
              Add to your home screen for the full app experience.
            </div>
            <div className="mt-3 flex gap-2">
              <Button
                data-testid="pwa-install-button"
                size="sm"
                onClick={install}
                className="rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50 h-8 px-4 text-xs"
              >
                <Download size={13} strokeWidth={1.5} className="mr-1.5" />
                Install
              </Button>
              <Button
                data-testid="pwa-dismiss-button"
                size="sm"
                variant="ghost"
                onClick={dismiss}
                className="rounded-full text-stone-500 hover:text-stone-900 hover:bg-stone-100 h-8 px-3 text-xs"
              >
                Not now
              </Button>
            </div>
          </div>
          <button
            aria-label="Close"
            onClick={dismiss}
            data-testid="pwa-close-button"
            className="text-stone-400 hover:text-stone-700 -mt-1 -mr-1"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {iosTipOpen && (
        <div
          data-testid="pwa-ios-tip"
          className="fixed inset-0 z-[60] bg-stone-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => setIosTipOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-stone-50 rounded-3xl border border-stone-200 max-w-sm w-full p-6 shadow-xl"
          >
            <div className="text-xs uppercase tracking-[0.2em] text-amber-700 mb-2">
              Install on iPhone / iPad
            </div>
            <div className="font-serif text-2xl text-stone-900 leading-tight">
              Two quick taps.
            </div>
            <ol className="mt-4 space-y-3 text-sm text-stone-700">
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-amber-700 text-stone-50 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                  1
                </span>
                <span>
                  Tap the <Share size={13} className="inline mx-1 mb-0.5" strokeWidth={1.5} />
                  <strong>Share</strong> button in Safari.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="h-6 w-6 rounded-full bg-amber-700 text-stone-50 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                  2
                </span>
                <span>
                  Choose <strong>Add to Home Screen</strong>.
                </span>
              </li>
            </ol>
            <Button
              onClick={() => setIosTipOpen(false)}
              className="mt-6 w-full rounded-full bg-amber-700 hover:bg-amber-800 text-stone-50"
            >
              Got it
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
